import {
  Transaction,
  TransactionStatus as UITransactionStatus,
} from "@/entities/transaction/model/types";
import { centsToDollars, dollarsToCents } from "@/shared/lib/format";
import {
  TransactionStatus as APITransactionStatus,
  TransactionOutputSchema,
  TransactionShortOutputSchema,
  TransactionUpdateSchema,
} from "./transactions";

/**
 * Map API TransactionShortOutputSchema to UI Transaction type
 */
export function mapTransactionShortToUI(apiTransaction: TransactionShortOutputSchema): Transaction {
  return {
    id: apiTransaction.internalId,
    externalId: apiTransaction.externalId,
    amount: centsToDollars(apiTransaction.amount),
    downPayment: centsToDollars(apiTransaction.downPayment),
    refundedAmount: centsToDollars(apiTransaction.refundedAmount),
    status: normalizeTransactionStatus(apiTransaction.status),
    location: apiTransaction.locationName,
    createdAt: apiTransaction.createdAt,
    updatedAt: apiTransaction.updatedAt,
    signUrl: apiTransaction.signUrl || undefined,
    receiptUrl: apiTransaction.receiptUrl || undefined,
    offer: apiTransaction.offer
      ? {
          id: apiTransaction.offer.id,
          type: apiTransaction.offer.type === "FIXED_APR" ? "Fixed APR" : "SAC",
          duration: apiTransaction.offer.duration,
          downPayment: centsToDollars(apiTransaction.offer.downPayment),
          apr: apiTransaction.offer.apr,
          amount: apiTransaction.offer.amount
            ? centsToDollars(apiTransaction.offer.amount)
            : undefined,
          financedAmount: apiTransaction.offer.financedAmount
            ? centsToDollars(apiTransaction.offer.financedAmount)
            : undefined,
          monthlyPayment: apiTransaction.offer.monthlyPayment
            ? centsToDollars(apiTransaction.offer.monthlyPayment)
            : undefined,
        }
      : undefined,
  };
}

/**
 * Map API TransactionOutputSchema to UI Transaction type
 */
export function mapTransactionToUI(apiTransaction: TransactionOutputSchema): Transaction {
  return {
    id: apiTransaction.internalId,
    externalId: apiTransaction.externalId,
    amount: centsToDollars(apiTransaction.amount),
    downPayment: centsToDollars(apiTransaction.downPayment),
    refundedAmount: centsToDollars(apiTransaction.refundedAmount),
    status: normalizeTransactionStatus(apiTransaction.status),
    location: apiTransaction.locationName,
    createdAt: apiTransaction.createdAt,
    updatedAt: apiTransaction.updatedAt,
    signUrl: apiTransaction.signUrl || undefined,
    receiptUrl: apiTransaction.receiptUrl || undefined,
    offer: apiTransaction.offer
      ? {
          id: apiTransaction.offer.id,
          type: apiTransaction.offer.type === "FIXED_APR" ? "Fixed APR" : "SAC",
          duration: apiTransaction.offer.duration,
          downPayment: centsToDollars(apiTransaction.offer.downPayment),
          apr: apiTransaction.offer.apr,
          amount: apiTransaction.offer.amount
            ? centsToDollars(apiTransaction.offer.amount)
            : undefined,
          financedAmount: apiTransaction.offer.financedAmount
            ? centsToDollars(apiTransaction.offer.financedAmount)
            : undefined,
          monthlyPayment: apiTransaction.offer.monthlyPayment
            ? centsToDollars(apiTransaction.offer.monthlyPayment)
            : undefined,
        }
      : undefined,
    refunds: apiTransaction.refunds.map((refund) => ({
      internalId: refund.internalId,
      externalId: refund.externalId,
      amount: centsToDollars(refund.amount),
      createdAt: refund.createdAt,
      status: refund.status,
    })),
    effectiveAt: apiTransaction.effectiveAt || undefined,
    signedAt: apiTransaction.signedAt || undefined,
    paidAt: apiTransaction.paidAt || undefined,
    paidAmount: centsToDollars(apiTransaction.paidAmount),
  };
}

/**
 * Normalize transaction status from API to UI format
 */
function normalizeTransactionStatus(status: APITransactionStatus): UITransactionStatus {
  return status as UITransactionStatus;
}

/**
 * Map UI Transaction to API TransactionUpdateSchema
 */
export function mapTransactionToUpdate(transaction: Partial<Transaction>): TransactionUpdateSchema {
  let apiStatus: APITransactionStatus | null = null;
  if (transaction.status) {
    apiStatus = transaction.status as APITransactionStatus;
  }

  return {
    amount: transaction.amount !== undefined ? dollarsToCents(transaction.amount) : null,
    downPayment:
      transaction.downPayment !== undefined ? dollarsToCents(transaction.downPayment) : null,
    status: apiStatus,
    effectiveAt: transaction.effectiveAt || null,
  };
}
